import { useParams } from "react-router-dom";
import { useFoodpilotContext } from "src/context/FoodpilotContext.tsx";
import { useGetOneScore } from "src/api/sure-api/score/useGetOneScore.ts";
import { ScoreDetailContextProvider } from "src/context/ScoreDetail/provider.tsx";
import { Loading } from "src/components/utils/Loading.tsx";
import { useGetOneCompany } from "src/api/client-api/company/useGetOneCompany";
import { useEntityBasedScoreParams } from "src/context/EntityBasedScore/useEntityBasedScoreParams";
import { useGetCompanyPost } from "src/api/client-api/post/useGetCompanyPost";
import { ScoreDetailBuilder } from "src/context/ScoreDetail/ScoreDetailBuilder";
import { URLHandler } from "src/services/URLHandler";
import { useCompanyScores } from "src/context/Company/Scores/useCompanyScores";

type CompanyScoreContextProviderProps = {
    children: JSX.Element | JSX.Element[];
};

const CompanyScoreContextProvider = (props: CompanyScoreContextProviderProps) => {
    const { children } = props;
    const { selectedCompany, currentCampaign } = useFoodpilotContext();
    const { scoreId: scoreIdString } = useParams<{ scoreId: string }>();
    const scoreId = Number(scoreIdString);
    const { score: detailedScore } = useGetOneScore(scoreId);
    const {
        ssq: { ssq },
        companyScores,
        scoreState,
    } = useCompanyScores();

    const { query, state } = useEntityBasedScoreParams({
        campaign: currentCampaign?.id,
        initDimensions: {
            dimensions: [["headings", "subheadings", "properties", "subproperties", "multivalues"]],
        },
        initVariations: scoreState.state.selectedVariations,
    });

    const { company } = useGetOneCompany({
        companyId: selectedCompany?.id,
        scoreQuery: query,
        statistics: true,
    });

    const { scores, scoresMax, scoresMin, scoresAvg, scoresCount, prevScores } = company || {};

    const {
        post,
        isFetched: isPostFetched,
        isRefetching: isPostRefetching,
    } = useGetCompanyPost({
        companyId: company?.id,
        campaignId: currentCampaign?.id,
    });

    if (!company || !detailedScore || !scores) {
        return <Loading />;
    }
    if (isPostFetched === false && isPostRefetching === true) {
        return <Loading />;
    }
    if (post === undefined) return <>{"Post not found"}</>;

    if (ssq === undefined) {
        return null;
    }
    const scoreDetailBuilder = new ScoreDetailBuilder({
        ssq,
        post: post,

        scoreId: detailedScore.id,
        scorePrecision: detailedScore.precision,
        scores: { scores, scoresMax, scoresMin, scoresAvg, scoresCount, prevScores },
    });

    return (
        <ScoreDetailContextProvider
            allScores={companyScores}
            prevCampaign={company.prevCampaignName}
            scores={scores}
            scoreDetailBuilder={scoreDetailBuilder}
            scoreDetail={detailedScore}
            goBackLink={URLHandler.company.scores(company.id)}
            scoreState={state}
            tabConfig={{
                distribution: {
                    link: (scoreId) => {
                        const url = URLHandler.company.scoreDistribution(company.id, scoreId.toString());
                        return url;
                    },
                },
                comparison: {
                    link: (scoreId) => {
                        const url = URLHandler.company.scoreComparison(company.id, scoreId.toString());
                        return url;
                    },
                },
                evolution: {
                    link: (scoreId) => {
                        const url = URLHandler.company.scoreEvolution(company.id, scoreId.toString());
                        return url;
                    },
                },
            }}
        >
            {children}
        </ScoreDetailContextProvider>
    );
};

export default CompanyScoreContextProvider;
